import { useEffect, useState, useRef } from 'react';

interface UseMaterialSymbolsOptions {
	timeout?: number;
	fontConfig?: {
		opsz?: string;
		wght?: string;
		FILL?: string;
		GRAD?: string;
	};
}

export function useMaterialSymbolsDynamic(options: UseMaterialSymbolsOptions = {}) {
	const {
		timeout = 2000,
		fontConfig = {
			opsz: '20..48',
			wght: '400',
			FILL: '0',
			GRAD: '0'
		}
	} = options;

	const [fontLoaded, setFontLoaded] = useState(false);
	const [iconsDetected, setIconsDetected] = useState<string[]>([]);
	const styleElementRef = useRef<HTMLStyleElement | null>(null);

	useEffect(() => {
		const detectIcons = () => {
			const iconElements = document.querySelectorAll('.material-symbols-outlined');
			const icons = new Set<string>();

			iconElements.forEach((el) => {
				const iconText = el.textContent?.trim();
				if (iconText) {
					icons.add(iconText);
				}
			});

			return Array.from(icons);
		};

		const loadFontSubset = (icons: string[]) => {
			if (icons.length === 0) {
				console.warn('Nenhum ícone Material Symbols detectado');
				setFontLoaded(true);
				return;
			}
			setIconsDetected(icons);

			if (styleElementRef.current) {
				styleElementRef.current.remove();
			}

			const textParam = icons.join('');
            const fontUrl = `https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@${fontConfig.opsz},${fontConfig.wght},${fontConfig.FILL},${fontConfig.GRAD}&text=${encodeURIComponent(textParam)}&display=block`;
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = fontUrl;
            link.onload = () => {
                setFontLoaded(true);
            };
            link.onerror = (error) => {
                console.error('Erro ao carregar fonte subset:', error);
                setFontLoaded(true);
            };

            document.head.appendChild(link);
            styleElementRef.current = link as any;

            const timeoutId = setTimeout(() => {
                setFontLoaded(true);
            }, timeout);

            if ('fonts' in document) {
                document.fonts.ready.then(() => {
                    clearTimeout(timeoutId);
                    setFontLoaded(true);
                });
            }
        };

        const detectAndLoad = () => {
            const icons = detectIcons();
            if (icons.length > 0) {
                loadFontSubset(icons);
            } else {
                setTimeout(() => {
                    const retryIcons = detectIcons();
                    if (retryIcons.length > 0) {
                        loadFontSubset(retryIcons);
                    } else {
                        console.warn('Nenhum ícone encontrado após retry');
                        setFontLoaded(true);
                    }
                }, 100);
            }
        };

        const timerId = setTimeout(detectAndLoad, 50);

        return () => {
            clearTimeout(timerId);
            if (styleElementRef.current) {
                styleElementRef.current.remove();
            }
        };
    }, [timeout, fontConfig.opsz, fontConfig.wght, fontConfig.FILL, fontConfig.GRAD]);

    return {
        fontLoaded,
        iconsDetected,
        isLoading: !fontLoaded
    };
}